import React from "react";
import useUploadFiles from "../../../hooks/useUploadFiles";

// components

import Upload from "../../../component/modals/upload";
import TextInput from "../../../component/element/forms/TextInput";
import DateInput from "../../../component/element/forms/DateInput";
import Modal from "../../../component/ui/modal";

// state
import { useAppDispatch, useAppSelector } from "../../../store";
import { IUploadCompanyFiles } from "../../../store/types/companies";
import { upload_company_files } from "../../../services/apis/companies";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

type IUploadDocument = {
  visible: boolean;
  onHandler: () => void;
};

type UploadDocumentFormData = Omit<
  IUploadCompanyFiles,
  "issuing_date" | "expiring_date"
> & {
  issuing_date: string;
  expiring_date: string;
};

const UploadDocument: React.FC<IUploadDocument> = ({ visible, onHandler }) => {
  const { id } = useParams();
  const companyId = Number(id);
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.companies.loading);
  const { loader, uploadFile, deleteFiles } = useUploadFiles();
  const [data, dataSet] = React.useState<UploadDocumentFormData>({
    name: "",
    document_file: "",
    issuing_body: "",
    expiring_date: "",
    issuing_date: "",
  });
  const [error, errorSet] = React.useState({
    document_file: "",
    expiring_date: "",
    issuing_body: "",
    issuing_date: "",
    name: "",
  });

  const handleOk = async () => {
    const errors = {
      document_file: data.document_file ? "" : "Upload a document.",
      name: data.name.trim() ? "" : "Document name is required.",
      issuing_body: data.issuing_body.trim() ? "" : "Issuing body is required.",
      issuing_date: data.issuing_date ? "" : "Issuing date is required.",
      expiring_date: data.expiring_date ? "" : "Expiry date is required.",
    };
    errorSet(errors);
    if (Object.values(errors).some(Boolean)) return;
    if (!Number.isInteger(companyId) || companyId <= 0) {
      toast.error("A valid company ID is required to upload a document.");
      return;
    }

    const payload: IUploadCompanyFiles = {
      ...data,
      issuing_date: data.issuing_date.slice(0, 10),
      expiring_date: data.expiring_date.slice(0, 10),
    };
    const succeeded = await upload_company_files(dispatch, payload, companyId);
    if (succeeded) onHandler();
  };

  const onChangeText = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    dataSet((prev) => ({ ...prev, [name]: value }));
    errorSet((prev) => ({ ...prev, [name]: "" }));
  };

  return (
    <Modal
      title="Upload Document"
      open={visible}
      onClose={onHandler}
      onOkClick={handleOk}
      variant="medium"
      isLoading={loading}
    >
      <div className="w-full ">
        <div className="">
          <Upload
            label="PDF"
            title="Upload Company Document"
            name="documents"
            loading={loader}
            status={error.document_file}
            completed={Boolean(data.document_file)}
            onChange={async (fileOrFiles) => {
              const paths = await uploadFile(fileOrFiles as File, "documents");
              if (paths.length > 0) {
                const file_path = paths[0]?.base_url + "/" + paths[0]?.path;
                dataSet((prev) => ({ ...prev, document_file: file_path }));
                errorSet((prev) => ({ ...prev, document_file: "" }));
              }
            }}
            onDelete={async () => {
              if (data.document_file) {
                const res = await deleteFiles(data.document_file);
                if (res) {
                  dataSet((prev) => ({ ...prev, document_file: "" }));
                  errorSet((prev) => ({ ...prev, document_file: "" }));
                }
              }
            }}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <TextInput
          label="Document Name"
          name="name"
          onChange={onChangeText}
          placeholder="Document Name"
          type="text"
          value={data.name}
          status={error.name}
        />

        <TextInput
          label="Issuing Body"
          name="issuing_body"
          onChange={onChangeText}
          placeholder="Issuing Body"
          type="text"
          value={data.issuing_body}
          status={error.issuing_body}
        />

        <DateInput
          label="Issuing Date"
          value={data.issuing_date}
          error={error.issuing_date}
          onChange={(date) => {
            dataSet((prev) => ({ ...prev, issuing_date: date }));
            errorSet((prev) => ({ ...prev, issuing_date: "" }));
          }}
        />

        <DateInput
          label="Expiring Date"
          value={data.expiring_date}
          error={error.expiring_date}
          onChange={(date) => {
            dataSet((prev) => ({ ...prev, expiring_date: date }));
            errorSet((prev) => ({ ...prev, expiring_date: "" }));
          }}
        />
      </div>
    </Modal>
  );
};

export default UploadDocument;
